import classes from "app/css/widgets/FileInput.module.less";
import locale from "common/managers/locale";
import GlobalFileDragAndDrop from "app/widgets/FileInput/GlobalFileDragAndDrop";
import FileInputState from "common/enums/FileInputState";
import GridList from "app/widgets/GridList/GridList";
import stringUtil from "common/helper/stringUtil";
import fileSizeFormatter from "common/helper/fileSizeFormatter";
import BaseWidget from "common/widgets/base/BaseWidget";
import DFButton from "app/widgets/DFButton/DFButton";
import FileInputModel from "common/models/FileInputModel";
import Link from "common/widgets/Link/Link";
import loginProps from "common/managers/loginProps";

const globalFileDragAndDrop = new GlobalFileDragAndDrop();

const FileInput = BaseWidget(kendo.ui.Widget).extend({
    options: {
        name: "FileInput",
        // Should the pager text be displayed
        pager: false,
        dataSource: [],

        // Should the file list grow to fill as much space as possible or be capped at 200px
        growable: false,

        // locale key for custom instructions displayed next to the button
        instructionsKey: null,

        // instruction text
        instructions: null,

        //Max file size in bytes or "REPOSITORY" if it should be pulled from the repository system property.
        maxFileSize: null
    },

    events: [
        "dataBinding",
        "dataBound",
        "view"
    ],

    _inProgress: false,
    _enabled: true,
    _multiple: true,
    _dragAndDropHandle: null,
    _allowDelay: false,

    //The different parts of the widget
    wrapper: null,
    filePicker: null,
    button: null,
    list: null,
    _visibleItems: 0,

    init: function() {
        kendo.ui.Widget.fn.init.apply(this, arguments);
        this._id = this.element.attr("id");
        if(!this._id) {
            throw new Error("FileInput widget requires an ID.");
        }
        this.element
            .attr("type", "file")
            .attr("data-message", "required.file")
            .hide();
        this._multiple = this.element.is("[multiple]");
        this._visibleItems = 0;
        this.element.on("change.FileInput", this._change.bind(this));
        this._wrapper();
        this._filePicker();
        this._button();
        this._instructions();
        this._dragAndDrop();
        this._list();
        this._count();
        this._finishedLoading = this._finishedLoading.bind(this);
        this._loading = this._loading.bind(this);
        this._error = this._error.bind(this);
        this.refresh = this.refresh.bind(this);
        this.setDataSource(this.options.dataSource);
    },

    destroy() {
        kendo.ui.Widget.fn.destroy.apply(this, arguments);
        this.element.off();
        if(this._dragAndDropHandle) {
            this._dragAndDropHandle.stop();
            delete this._dragAndDropHandle;
        }
        this._cleanupDS();
        delete this.dataSource;
        this.list.destroy();
        this.button.destroy();
    },

    setOptions() {
        kendo.ui.Widget.fn.setOptions.apply(this, arguments);
        this._setInstructionsText();
    },

    _wrapper() {
        this.element.wrap("<div>");
        this.wrapper = this.element.parent();
        this.wrapper
            .attr("role", "group")
            .attr("aria-labelledby", this.element.attr("aria-labelledby"))
            .addClass(classes.fileUpload)
            .toggleClass(classes.growable, !!this.options.growable)
            .toggleClass(classes.single, !this._multiple);
    },

    _filePicker() {
        this.filePicker = $("<div>")
            .addClass(classes.filePicker)
            .attr("data-expand-validation", "true")
            .attr("role", "presentation")
        this.wrapper.append(this.filePicker);
    },

    _button() {
        const $button = $("<button>")
            .attr("aria-describedby", `${this._id}_instructions`)
            .addClass("secondary")
            .addClass(classes.fileUploadButton);
        this.filePicker.append($button);
        $("<span>")
            .text(locale.get(this._multiple ? "choose.files" : "choose.file"))
            .addClass("buttonText") //needed by themes
            .appendTo($button);
        this.button = new DFButton($button, {
            click: (e) => {
                this.element.click();
                e.preventDefault();
            },
            blurred: () => {
                this._triggerValidation();
            }
        });
    },

    getARIAElements() {
        return $(this.button.element, this.list.element);
    },

    _instructions() {
        this.instructions = $("<div>")
            .attr("id", `${this._id}_instructions`)
            .addClass(classes.instructions);
        this._setInstructionsText();
        this.filePicker.append(this.instructions);
    },

    _setInstructionsText() {
        if(this.options.instructionsKey) {
            this.instructions.text(locale.get(this.options.instructionsKey));
        } else if(this.options.instructions) {
            this.instructions.text(this.options.instructions);
        } else if(this._multiple) {
            this.instructions.text(locale.get("drop.files"));
        } else {
            this.instructions.text(locale.get("drop.file"));
        }
    },

    _dragAndDrop() {
        this._dragAndDropHandle = globalFileDragAndDrop.listen(this.filePicker, this._multiple,files => {
            this._addFiles(files);
            this.focus();
        });
    },

    _stateToClass(state) {
        switch(state) {
            case FileInputState.SUCCESS:
                return classes.success;
            case FileInputState.ERROR:
                return classes.error;
            case FileInputState.EXISTING:
                return classes.existing;
            case FileInputState.READY:
            default:
                return classes.ready;
        }
    },

    _list() {
        const $list = $("<div>")
            .attr("id", `${this._id}_list`)
            .attr("aria-label", locale.get("Files"))
            .addClass(classes.fileList);
        this.wrapper.append($list);
        this.list = new GridList($list, {
            autoBind: true,
            listItem: {
                removeClass: classes.removed,
                focusedClass: classes.focusedRow,
                attrs: {
                    "class": item => {
                        const c = [classes.listItem, this._stateToClass(item.get("state"))];
                        if(item.canDelete) {
                            c.push(classes.canDelete);
                        }
                        if(item.canView) {
                            c.push(classes.canView);
                        }
                        return c.join(" ");
                    }
                }
            },
            cells: [
                {
                    name: "removedMessage",
                    template: `<span>${locale.get("file.removed")}</span>`,
                    attrs: {
                        "class": classes.removedMessage
                    }
                },
                [
                    {
                        template: "#:name#",
                        attrs: {
                            "class": classes.name
                        }
                    },
                    {
                        link: {
                            attrs: {
                                'text': '#:name#'
                            }
                        },
                        attrs: {
                            "class": classes.download
                        }
                    },
                    {
                        template: item => stringUtil.escapeHtml(fileSizeFormatter.format(item.size)),
                        attrs: {
                            "class": classes.size
                        }
                    },
                    {
                        template: item => {
                            if(item.state === FileInputState.SUCCESS) {
                                return `<span title="${locale.get("request.success")}">
                                            <span aria-hidden="true" class="${classes.successIcon}"></span>
                                        </span>`;
                            }
                            return "";
                        },
                        attrs: {
                            "class": classes.status
                        }
                    },
                    {
                        command: {
                            name: "delete"
                        },
                        attrs: {
                            "class": classes.remove
                        }
                    }
                ],
                {
                    name: "error",
                    template: `<span title="${locale.get("error.title")}">
                                    <span aria-hidden="true" class="${classes.errorIcon}"></span>
                               </span>
                               <span>#if(data.errorMessage){##:errorMessage##}#</span>`,
                    attrs: {
                        "class": classes.errorMessage
                    }
                }
            ]
        });
        this.list.bind("dataBinding", e => {
            const items = e.removedItems || this.list.items();
            for(let i = 0; i < items.length; i++) {
                kendo.destroy(items[i]);
            }
            this._visibleItems -= items.length;
        });
        this.list.bind("dataBound", e => {
            const dataItems = e.addedDataItems || this.list.dataItems();
            const items = e.addedItems || this.list.items();
            for(let i = 0; i < items.length; i++) {
                const file = dataItems[i];
                const a = $(items[i]).find(`.${classes.download} > a`);
                new Link(a, {
                    click: () => {
                        this.trigger("view", {file});
                    }
                });
            }
            this._visibleItems += items.length;
            this.refresh();
        });
        this.list.bind("delayRemove", e => {
            if(!this._allowDelay) {
                e.preventDefault();
            }
        });
        this.list.bind("delete", e => this._delete(e.model));
    },

    _isServerDelete(model) {
        return (model.get("state") === FileInputState.EXISTING || model.get("state") === FileInputState.SUCCESS)
            && model.get("canDelete");
    },

    _isLocalDelete(model) {
        return model.get("state") === FileInputState.READY || model.get("state") === FileInputState.ERROR;
    },

    _delete(model) {
        if(this._enabled && (this._isLocalDelete(model) || this._isServerDelete(model))) {
            this._allowDelay = true;
            this.dataSource.remove(model);
            this._allowDelay = false;
            this._updateCount();
        }
    },

    _count() {
        this.count = $("<div></div>", {
            id: `${this._id}_pager`,
            "class": `${classes.count} ${classes.noItems}`
        });
        if(this.options.pager && this._multiple) {
            this.wrapper.append(this.count);
        }
    },

    _updateCount() {
        const count = this.dataItems()
            .map(f => f.get("state"))
            .filter(s => s !== FileInputState.SUCCESS)
            .length;
        this.count.text(locale.get("main.upload.total", count));
        this.count.toggleClass(classes.noItems, count === 0);
        this.setDescribedBy("FILE_COUNT", count > 0 ? this.count.attr("id") : "");
        this.wrapper.toggleClass(classes.empty, this._visibleItems === 0);
    },

    enable(enabled) {
        this._enabled = !!enabled;
        this.button.enable(this._enabled);
        this.wrapper.toggleClass(classes.disabled, !this._enabled);
    },

    initFocusableEvents: function(onFocus, onBlur) {
        this.button.bind("focused", onFocus);
        this.button.bind("blurred", onBlur);
        this.list.bind("focused", onFocus);
        this.list.bind("blurred", onBlur);
    },

    destroyFocusable: function(onFocus, onBlur) {
        this.button.unbind("focused", onFocus);
        this.button.unbind("blurred", onBlur);
        this.list.unbind("focused", onFocus);
        this.list.unbind("blurred", onBlur);
    },

    focus() {
        this.button.focus();
    },

    refresh() {
        this.trigger("dataBinding");
        this._updateCount();
        this.wrapper.toggleClass(classes.fileSelected, this._visibleItems > 0);
        this.trigger("dataBound");
    },

    _change(e) {
        this._addFiles([].slice.apply(e.target.files))
        e.target.value = null;
        this.focus();
    },

    _maxSize() {
        if(this.options.maxFileSize === null) {
            return Number.POSITIVE_INFINITY;
        } else if(this.options.maxFileSize === "REPOSITORY") {
            return loginProps.get().maxUploadSize;
        } else {
            return this.options.maxFileSize;
        }
    },

    _addError(file, errorMessage) {
        const model = new FileInputModel({
            id: kendo.guid(),
            name: file.name,
            size: file.size,
            state: FileInputState.ERROR,
            errorMessage
        });
        model.dirty = false;
        this.dataSource.add(model);
    },

    _addFiles(files) {
        if(this._enabled && (this._multiple || files.length === 1)) {
            if(!this._multiple) {
                this.dataSource.data().slice().forEach(f => this.dataSource.remove(f));
            }
            const extensions = this._extensions();
            const maxSize = this._maxSize();
            const valid = [];
            files.forEach(file => {
                const fileName = file.name.toLowerCase();
                if(extensions.length > 0 && !(extensions.some(ext => fileName.endsWith(ext))))  {
                    this._addError(file,locale.get("invalid.extension"));
                } else if(file.size > maxSize) {
                    this._addError(file, locale.get("invalid.filesize", fileSizeFormatter.format(maxSize)));
                } else if(file.size === 0) {
                    this._addError(file, locale.get("invalid.empty.file"));
                } else {
                    valid.push(file);
                }
            });
            this.dataSource.addFiles(valid);
            this._triggerValidation();
        }
    },

    _extensions() {
        const accept = this.element.attr("accept") || "";
        return accept.split(",")
            .map(ext => ext.toLowerCase())
            .filter(ext => /^\.[a-z]+$/.test(ext));
    },

    _triggerValidation() {
        this.element.trigger("blur");
    },

    _cleanupDS() {
        if(this.dataSource) {
            this.dataSource.unbind("requestStart", this._loading);
            this.dataSource.unbind("requestEnd", this._finishedLoading);
            this.dataSource.unbind("error", this._error);
        }
    },

    setDataSource(dataSource) {
        this._cleanupDS();
        this.dataSource = kendo.data.DataSource.create(dataSource);
        this.dataSource.bind("requestStart", this._loading);
        this.dataSource.bind("requestEnd", this._finishedLoading);
        this.dataSource.bind("error", this._error);
        this.list.setDataSource(this.dataSource);
    },

    dataItems() {
        return this.dataSource.flatView();
    },

    items() {
        return [];
    },

    _loading(e) {
        if(e.type === "create") {
            this._loadingState(true);
        }
    },

    _error() {
        this._loadingState(false);
        const row = [].findIndex.call(this.dataItems(), f => f.get("state") === FileInputState.ERROR);
        this.list.focusCell(row, "error");
    },

    _finishedLoading(e) {
        if(e.type === "create") {
            this._loadingState(false);
        }
    },

    _loadingState(isLoading) {
        this._inProgress = isLoading;
        this.button.value(isLoading);
        this.list.enable(!isLoading);
    },

    valueForValidation() {
        const count = this.dataItems().length;
        return count === 0 ? null : count;
    }
});
kendo.ui.plugin(FileInput);
export default FileInput;
